import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { FileText, Download, Calendar, TrendingUp, CreditCard, Banknote, Receipt, RotateCcw, Ban } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ZReportPage = () => {
  const [report, setReport] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchReport();
  }, [selectedDate]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/z-report?date=${selectedDate}`);
      setReport(response.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load Z-Report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    window.open(`${API}/reports/z-report/export?date=${selectedDate}`, '_blank');
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">Loading Z-Report...</div>
      </div>
    );
  }

  return (
    <div data-testid="zreport-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Z-Report</h1>
          <p className="text-slate-500 mt-1 font-body">End of day sales summary</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-40"
              data-testid="zreport-date"
            />
          </div>
          <Button variant="outline" onClick={handleExport} data-testid="export-zreport-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" onClick={handlePrint}>
            <FileText className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {report && (
        <div className="print:p-0" id="z-report-content">
          {/* Header */}
          <Card className="print:shadow-none print:border-0">
            <CardHeader className="text-center border-b">
              <CardTitle className="font-heading text-2xl">
                Z-REPORT
              </CardTitle>
              <p className="text-lg font-medium">{new Date(report.date).toLocaleDateString('en-GB', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</p>
              <p className="text-sm text-slate-500">
                Generated: {new Date(report.generated_at).toLocaleString()} by {report.generated_by}
              </p>
            </CardHeader>
          </Card>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 print:grid-cols-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Revenue</p>
                    <p className="price-display text-3xl">£{report.summary.total_revenue.toFixed(2)}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total VAT</p>
                    <p className="price-display text-3xl">£{report.summary.total_vat.toFixed(2)}</p>
                  </div>
                  <Receipt className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Transactions</p>
                    <p className="font-heading text-3xl font-bold">{report.summary.total_transactions}</p>
                  </div>
                  <FileText className="w-8 h-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Avg. Transaction</p>
                    <p className="price-display text-3xl">£{report.summary.average_transaction.toFixed(2)}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Payment Methods & Cash Drawer */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">By Payment Method</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">Card/Contactless</span>
                  </div>
                  <div className="text-right">
                    <p className="price-display text-lg">£{report.by_payment_method.card.revenue.toFixed(2)}</p>
                    <p className="text-xs text-slate-500">{report.by_payment_method.card.transactions} transactions</p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Banknote className="w-5 h-5 text-green-600" />
                    <span className="font-medium">Cash</span>
                  </div>
                  <div className="text-right">
                    <p className="price-display text-lg">£{report.by_payment_method.cash.revenue.toFixed(2)}</p>
                    <p className="text-xs text-slate-500">{report.by_payment_method.cash.transactions} transactions</p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Receipt className="w-5 h-5 text-purple-600" />
                    <span className="font-medium">Account</span>
                  </div>
                  <div className="text-right">
                    <p className="price-display text-lg">£{report.by_payment_method.account.revenue.toFixed(2)}</p>
                    <p className="text-xs text-slate-500">{report.by_payment_method.account.transactions} transactions</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Cash Drawer</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between p-3 border-b">
                  <span>Cash Received</span>
                  <span className="price-display">£{report.cash_drawer.total_cash_received.toFixed(2)}</span>
                </div>
                <div className="flex justify-between p-3 border-b">
                  <span>Change Given</span>
                  <span className="price-display text-red-600">-£{report.cash_drawer.total_change_given.toFixed(2)}</span>
                </div>
                <div className="flex justify-between p-3 bg-slate-50 rounded-lg font-bold">
                  <span>Net Cash</span>
                  <span className="price-display">£{report.cash_drawer.net_cash.toFixed(2)}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Refunds & Voids */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg">Refunds & Voids</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <RotateCcw className="w-6 h-6 text-amber-600" />
                    <div>
                      <p className="font-medium">Refunds</p>
                      <p className="text-sm text-slate-500">{report.refunds.count} transactions</p>
                    </div>
                  </div>
                  <p className="price-display text-xl text-amber-700">£{report.refunds.total.toFixed(2)}</p>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Ban className="w-6 h-6 text-red-600" />
                    <div>
                      <p className="font-medium">Voids</p>
                      <p className="text-sm text-slate-500">{report.voids.count} transactions</p>
                    </div>
                  </div>
                  <p className="price-display text-xl text-red-700">£{report.voids.total.toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items Sold */}
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-sm text-slate-500">Total Items Sold</p>
                <p className="font-heading text-4xl font-bold">{report.summary.total_items_sold}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ZReportPage;
